import { useEffect } from "react";
import { useDispatch } from "react-redux";
import connectionMonitor from "../utils/connectionMonitor";
import { patchContactInfo, invalidateCachedContact } from "../redux/Slice/Inbox/inboxInfoSlice";

/**
 * Live-updates the inbox when a contact becomes linked to a Shopify customer.
 *
 * The link (contact.shopifyCustomerId) is written server-side by the Shopify customer sync, usually
 * while an agent already has that chat open — so the contact info held in Redux and in the two
 * contact caches was loaded BEFORE the link existed and keeps hiding the Shopify icon/orders until
 * the chat is reopened. The server now emits `shopify:customer:linked` the moment a contact is newly
 * linked; this hook (1) clears that contact's stale cached copy so the next load is fresh, and
 * (2) patches the open contact in Redux if it is the one that changed.
 *
 * Mounted once, by ChatInfo.jsx — the component that already owns the inbox's other live contact
 * sync. Subscribes through connectionMonitor (the same signaling-only pattern group_updated uses)
 * and always unsubscribes on unmount, so no listener is ever left behind or doubled.
 */
export default function useShopifyCustomerLinkSync() {
  const dispatch = useDispatch();

  useEffect(() => {
    const onLinked = (payload) => {
      const contactId = payload?.contactId;
      const shopifyCustomerId = payload?.shopifyCustomerId;
      if (!contactId || !shopifyCustomerId) return;
      invalidateCachedContact(contactId);
      dispatch(patchContactInfo({ contactId, changes: { shopifyCustomerId: String(shopifyCustomerId) } }));
    };
    connectionMonitor.on("shopify:customer:linked", onLinked);
    return () => connectionMonitor.off("shopify:customer:linked", onLinked);
  }, [dispatch]);
}
